import { create } from 'zustand';
import { scenarioRecorder } from './scenarioRecorder';
import type { RecorderSnapshot, RecordedStep, RecordingMode } from './scenarioRecorder';
import { recorderDiagnosticsLog } from './recorderDiagnosticsLog';
import { RecorderEdgeCases } from './recorderEdgeCases';
import { linkOrphanSteps } from './orphanStepLinker';

/**
 * User-facing privacy toggles for the archive (spec §19). Presentation-layer
 * shape; the archive layer maps it to the redaction sanitizer's options. Kept
 * here (not imported from `archive/`) so the recording → archive dependency
 * stays one-directional.
 */
export interface RecorderPrivacyOptions {
  /** Strip absolute host paths from every file. */
  excludeAbsolutePaths: boolean;
  /** Mask the directory part of absolute paths, keeping file names. */
  maskLocalDirectories: boolean;
  /** Mask the recording user's name everywhere. */
  maskUsername: boolean;
  /** Do not bundle the model copy. */
  disableModelSave: boolean;
  /** Drop large gRPC/element payloads (keep stub refs). */
  disableBigPayloadSave: boolean;
}

/**
 * Default privacy posture: faithful capture (nothing masked), opt-in redaction.
 * The pre-download warning makes the sensitivity explicit so the user chooses
 * what to strip (spec §26 Q4 — default surfaced as a setting).
 */
export const DEFAULT_PRIVACY_OPTIONS: RecorderPrivacyOptions = {
  excludeAbsolutePaths: false,
  maskLocalDirectories: false,
  maskUsername: false,
  disableModelSave: false,
  disableBigPayloadSave: false,
};

/**
 * Archive download seam. The archive-builder card registers the real
 * zip-and-download implementation via {@link setScenarioArchiveDownloader};
 * until then the default throws so the UI surfaces a clear "not available yet".
 */
export type ScenarioArchiveDownloader = (
  snapshot: RecorderSnapshot,
  meta: { name: string; description?: string },
  privacy?: RecorderPrivacyOptions,
) => Promise<void> | void;

const defaultDownloader: ScenarioArchiveDownloader = () => {
  throw new Error('Scenario download is not available yet.');
};

let archiveDownloader: ScenarioArchiveDownloader = defaultDownloader;

export function setScenarioArchiveDownloader(downloader: ScenarioArchiveDownloader | null): void {
  archiveDownloader = downloader ?? defaultDownloader;
}

interface RecorderUiState {
  isRecording: boolean;
  mode: RecordingMode;
  steps: RecordedStep[];
  /** True after stopping, while the user fills in name/description before download. */
  showStopForm: boolean;
  scenarioName: string;
  description: string;
  error: string | null;
  warnings: string[];
  /** True while the archive is being collected + zipped (drives the button spinner). */
  downloading: boolean;
  /** Privacy/redaction toggles applied to the archive before download. */
  privacy: RecorderPrivacyOptions;

  setMode: (mode: RecordingMode) => void;
  /** Flip a single privacy toggle. */
  setPrivacyOption: (key: keyof RecorderPrivacyOptions, value: boolean) => void;
  startRecording: () => void;
  requestStop: () => void;
  cancelStop: () => void;
  setScenarioName: (name: string) => void;
  setDescription: (description: string) => void;
  /** True when the scenario name passes the required-field validation. */
  canDownload: () => boolean;
  confirmDownload: () => Promise<boolean>;
  refresh: () => void;
  setWarnings: (warnings: string[]) => void;
  /** Append a single warning (e.g. an edge-case notice) without replacing the list. */
  addWarning: (warning: string) => void;
  /** Surface an error message to the user (e.g. an edge-case error). */
  setError: (message: string) => void;
  dismissError: () => void;
  reset: () => void;
}

export const useRecorderStore = create<RecorderUiState>((set, get) => ({
  isRecording: false,
  mode: 'full',
  steps: [],
  showStopForm: false,
  scenarioName: '',
  description: '',
  error: null,
  warnings: [],
  downloading: false,
  privacy: { ...DEFAULT_PRIVACY_OPTIONS },

  setMode: (mode) => {
    // Mode is locked while recording — changing it mid-session would split the
    // captured fidelity. Ignore (and surface nothing) when recording.
    if (get().isRecording) {
      return;
    }
    scenarioRecorder.setMode(mode);
    set({ mode });
  },

  setPrivacyOption: (key, value) =>
    set((state) => ({ privacy: { ...state.privacy, [key]: value } })),

  startRecording: () => {
    scenarioRecorder.start(get().mode);
    // Fresh diagnostics log per session so logs/recorder.log is populated (a
    // mandatory archive file — an empty one flags the archive INCOMPLETE).
    recorderDiagnosticsLog.clear();
    recorderDiagnosticsLog.recordingStarted(get().mode);
    set({
      isRecording: true,
      steps: [],
      showStopForm: false,
      scenarioName: '',
      description: '',
      error: null,
      warnings: [],
      privacy: { ...DEFAULT_PRIVACY_OPTIONS },
    });
  },

  requestStop: () => {
    scenarioRecorder.stop();
    // Card G: capture the final-state screenshot at stop (Full mode only, no-op
    // otherwise). Best-effort and fire-and-forget — it self-registers so
    // confirmDownload()'s flush awaits it; it must not block the stop form.
    void scenarioRecorder.captureFinalState();
    const snapshot = scenarioRecorder.getSnapshot();
    recorderDiagnosticsLog.recordingStopped(snapshot.steps.length);
    set({ isRecording: false, steps: snapshot.steps, showStopForm: true });
  },

  cancelStop: () => set({ showStopForm: false }),

  setScenarioName: (name) => set({ scenarioName: name }),
  setDescription: (description) => set({ description }),

  canDownload: () => get().scenarioName.trim().length > 0,

  confirmDownload: async () => {
    const { scenarioName, description, canDownload } = get();
    if (!canDownload()) {
      set({ error: 'A scenario name is required before downloading.' });
      return false;
    }
    set({ downloading: true, error: null });
    try {
      // Card G: drain any in-flight best-effort screenshot captures (key steps +
      // final-state) so the snapshot carries them before the archive is built.
      await scenarioRecorder.flushScreenshots();
      const snapshot = scenarioRecorder.getSnapshot();
      // Link UI<->gRPC orphans before archiving: mark suspect steps (so the gap
      // is visible in scenario.json) and surface each anomaly to the user. Never
      // blocks the download — a flagged recording still exports.
      const edge = new RecorderEdgeCases(recorderDiagnosticsLog, {
        onUserMessage: (outcome) =>
          outcome.severity === 'error'
            ? get().setError(outcome.userMessage)
            : get().addWarning(outcome.userMessage),
      });
      linkOrphanSteps(snapshot, edge);

      await archiveDownloader(
        snapshot,
        {
          name: scenarioName.trim(),
          description: description.trim() || undefined,
        },
        get().privacy,
      );
      set({ showStopForm: false, error: null });
      return true;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to download the scenario.' });
      return false;
    } finally {
      set({ downloading: false });
    }
  },

  refresh: () => {
    const snapshot: RecorderSnapshot = scenarioRecorder.getSnapshot();
    set({ isRecording: snapshot.isRecording, mode: snapshot.mode, steps: snapshot.steps });
  },

  setWarnings: (warnings) => set({ warnings }),
  addWarning: (warning) => set((state) => ({ warnings: [...state.warnings, warning] })),
  setError: (message) => set({ error: message }),
  dismissError: () => set({ error: null }),

  reset: () => {
    scenarioRecorder.reset();
    set({
      isRecording: false,
      mode: 'full',
      steps: [],
      showStopForm: false,
      scenarioName: '',
      description: '',
      error: null,
      warnings: [],
      downloading: false,
      privacy: { ...DEFAULT_PRIVACY_OPTIONS },
    });
  },
}));
